import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import sendClassSelection from 'core/application/adminDashboard/benefitDetails/design/qsehra/sendClassSelection'
import { EmployeeEligibilityType } from 'core/application/adminDashboard/benefitDetails/qsehra/benefitDesignState'

import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'
import Icon from 'common/components/general/Icon'

import useCommandRequest from 'common/hooks/useCommandRequest'

import { BaseComponentProps } from '../../common/models'

export type QsehraEmployeeClassesProps = {
  planPeriodId: string
  employeeEligibilityType?: EmployeeEligibilityType
  isClassSelectionEditable: boolean
}
type InitialValues = {
  employeeEligibilityType?: EmployeeEligibilityType
}

const EmployeeClasses: React.FC<BaseComponentProps<QsehraEmployeeClassesProps>> = (props) => {
  const { bindSubmitFormHandler } = props

  const { t: translate } = useTranslation()
  const [initialValues] = useState<InitialValues>({
    employeeEligibilityType: props.state.employeeEligibilityType,
  })
  const [sendCommand] = useCommandRequest()

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [initialValues])

  const onSubmit = async (values: { employeeEligibilityType: number }) => {
    const result = await sendCommand(() =>
      sendClassSelection({
        employeeEligibility: values.employeeEligibilityType,
        planPeriodId: props.state.planPeriodId,
      }),
    )

    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
        props.setIsFormValid(formValidator(translate).isValidSync(values))
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form onSubmit={handleSubmit} autoComplete="off">
            <Row className={'mb-32'}>
              <Col span={17} className={'benefit_design_page_header'}>
                <h5>{translate('admin-dashboard.benefit-details.qsehra-benefit-eligible-classes.header')}</h5>

                <Alert
                  message={translate('admin-dashboard.benefit-details.qsehra-benefit-eligible-classes.alert-text')}
                  type="info"
                  showIcon
                  icon={<Icon className="far fa-info-circle" />}
                  className="mb-32"
                />
              </Col>
              <Col span={17} className={'benefit_design_page_header'}>
                <Radio.Group
                  name="employeeEligibilityType"
                  className={'w-100 qsehra-benefit-design-radio-group'}
                  size={'large'}
                  defaultValue={props.state.employeeEligibilityType}
                  disabled={!props.state.isClassSelectionEditable}
                >
                  <Row gutter={8}>
                    <Col span={24} className={'mb-16'}>
                      <Radio.Button value={EmployeeEligibilityType.FullTimeOnly} className={'w-100'}>
                        Full-time employees only.
                      </Radio.Button>
                    </Col>
                    <Col span={24} className={'mb-16'}>
                      <Radio.Button value={EmployeeEligibilityType.FullTimeAndPartTime} className={'w-100'}>
                        Full-time and part-time employees.
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    employeeEligibilityType: Yup.number().required(translate('general.required')).nullable(),
  })

export default EmployeeClasses
